import { useState } from "react";
import {
  Button,
  CardGroup,
  CardGroupCheckboxCardProps,
  CardGroupSwitchCardProps,
  Dialog,
  Skeleton,
  Typography,
  useFlags,
} from "@inductiveautomation/ws-components";
import { useTranslation } from "react-i18next";
import { AccountFlags } from "../../featureFlags.ts";

import {
  EmailPreferenceCard,
  EmailPreferencesFormProps,
  EmailNewsletterId,
  EmailTranslationKey,
  HelperMessageTranslationKey,
} from "./EmailPreferencesForm.types";
import { getEmailPreferencesFormStyles } from "./_emailPreferencesForm.styles";

const applicationCards = (
  workspaceFeatureFlag: boolean
): EmailPreferenceCard[] => [
  { id: "email-preferences-account", translationKey: "account" },
  { id: "email-preferences-support", translationKey: "support" },
  ...(workspaceFeatureFlag
    ? ([
        { id: "email-preferences-workspace", translationKey: "workspaces" },
        {
          id: "email-preferences-workspace-activity",
          translationKey: "workspace_activity",
        },
      ] as EmailPreferenceCard[])
    : []),
];

const marketingCards: EmailPreferenceCard[] = [
  { id: "email-preferences-conference", translationKey: "conference" },
  { id: "email-preferences-integrators", translationKey: "integrators" },
  { id: "email-preferences-newsfeed", translationKey: "newsfeed" },
  { id: "email-preferences-webinars", translationKey: "webinars" },
];

const feedbackCards: EmailPreferenceCard[] = [
  { id: "email-preferences-user-feedback", translationKey: "user_feedback" },
];

const EmailPreferencesForm = ({
  inputType,
  selectedIds = [],
  onChange,
  onUnsubscribeAll,
  onSave,
  idToDisable,
}: EmailPreferencesFormProps) => {
  const { classes } = getEmailPreferencesFormStyles();
  const { t } = useTranslation();
  const { tsAccountWorkspaceVisibilityFe } = useFlags<AccountFlags>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const unsubscribeAll = () => {
    onUnsubscribeAll();
    setIsModalOpen(false);
  };

  const hasHelperMessage = (
    key: EmailTranslationKey
  ): key is HelperMessageTranslationKey => key !== "workspace_activity";

  const mapEmailCard = (
    card: EmailPreferenceCard
  ): CardGroupCheckboxCardProps | CardGroupSwitchCardProps => ({
    id: card.id,
    inputType,
    label: t(`email_preferences.cards.${card.translationKey}.label`),
    description: t(
      `email_preferences.cards.${card.translationKey}.description`
    ),
    ...(hasHelperMessage(card.translationKey)
      ? {
          helperMessage: t(
            `email_preferences.cards.${card.translationKey}.helperMessage`
          ),
        }
      : {}),
    value: "",
    checked: selectedIds.includes(card.id),
    onValueChange: (id, value) => {
      onChange({ id: id as EmailNewsletterId, value });
    },
    disabled: idToDisable === card.id || idToDisable === "unsubscribe_all",
  });

  return (
    <>
      <div className={classes.cardContainer}>
        {idToDisable === "initial_load" ? (
          <>
            <Skeleton variant="card_group" count={4} />
            <Skeleton variant="card_group" count={4} />
          </>
        ) : (
          <>
            <CardGroup
              label={t("email_preferences.application_communications")}
              cards={applicationCards(tsAccountWorkspaceVisibilityFe).map(
                mapEmailCard
              )}
            />

            <CardGroup
              label={t("email_preferences.marketing_communications")}
              cards={marketingCards.map(mapEmailCard)}
            />

            <CardGroup
              label={t("email_preferences.feedback_communications")}
              cards={feedbackCards.map(mapEmailCard)}
            />
          </>
        )}
      </div>
      <div className={classes.actions}>
        {onSave && (
          <Button
            buttonVariant="primary"
            label={t("email_preferences.update_button")}
            onClick={onSave}
          />
        )}
        <Button
          buttonVariant={onSave ? "secondary" : "primary"}
          label={t("email_preferences.unsubscribe")}
          onClick={() => setIsModalOpen(true)}
        />
      </div>
      <Typography className={classes.footnote}>
        {`${t("email_preferences.privacy_policy_learn_more")} `}
        <a href="https://inductiveautomation.com/privacypolicy" target="_blank">
          {t("links.privacy_policy")}
        </a>
      </Typography>
      <Dialog
        showCloseButton
        contentArea={t("email_preferences.unsubscribe_confirmation")}
        headline={t("email_preferences.unsubscribe")}
        onClose={() => setIsModalOpen(false)}
        testId={"email-preferences-unsubscribe-dialog"}
        collapseContentScrollGutter
        isOpen={isModalOpen}
        actions={[
          {
            label: t("common.go_back"),
            type: "cancel",
          },
          {
            label: t("email_preferences.unsubscribe"),
            type: "submit",
            onClick: unsubscribeAll,
          },
        ]}
      />
    </>
  );
};

export default EmailPreferencesForm;
